import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Loader2, Trash2 } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { PageHeader } from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';

export default function AdminCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', isActive: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminService.getCategories()
      .then(data => {
        setCategories(data);
        setError('');
      })
      .catch((err) => {
        setCategories([]);
        setError(err.message || 'Greška pri učitavanju kategorija');
        toast.error(err.message || 'Greška pri učitavanju kategorija');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const openCreate = () => {
    setEditingCat(null);
    setFormData({ name: '', description: '', isActive: true });
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditingCat(cat);
    setFormData({ name: cat.name, description: cat.description, isActive: cat.isActive });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingCat) {
        const updated = await adminService.updateCategory(editingCat.id, formData);
        setCategories(prev => prev.map(c => c.id === editingCat.id ? updated : c));
        toast.success('Kategorija ažurirana');
      } else {
        const newCat = await adminService.createCategory(formData);
        setCategories(prev => [...prev, newCat]);
        toast.success('Kategorija kreirana');
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error('Greška pri cuvanju');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const updated = await adminService.toggleCategory(id);
      setCategories(prev => prev.map(c => c.id === id ? updated : c));
      toast.success('Status promenjen');
    } catch (err) {
      toast.error(err.message || 'Greška pri promeni statusa');
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminService.deleteCategory(id);
      setCategories(prev => prev.filter(c => c.id !== id));
      toast.success('Kategorija obrisana');
    } catch (e) {
      toast.error('Kategorija ne može da se obrise ako ima proizvode');
    }
  };

  return (
    <div>
      <PageHeader
        title="Kategorije"
        subtitle={`${categories.length} kategorija`}
        actions={
          <Button onClick={openCreate} size="sm" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary-light font-ui">
            <Plus size={16} className="mr-1.5" /> Nova kategorija
          </Button>
        }
      />

      {loading ? (
        <div className="space-y-3">
          {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : (
        <>
        {error && (
          <div className="mb-4 rounded-lg border border-border bg-card p-3">
            <p className="text-sm text-muted-foreground font-ui">{error}</p>
          </div>
        )}
        <div className="space-y-3">
          {categories.map(cat => (
            <motion.div
              key={cat.id}
              layout
              className="bg-card rounded-xl shadow-card border border-border p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            >
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <h3 className="font-serif text-base font-semibold text-foreground">{cat.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-ui ${
                    cat.isActive ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-muted text-muted-foreground border border-border'
                  }`}>
                    {cat.isActive ? 'Aktivna' : 'Neaktivna'}
                  </span>
                  <span className="badge-gold">{cat.productCount} proizvoda</span>
                </div>
                <p className="text-sm text-muted-foreground font-ui mt-1">{cat.description}</p>
              </div>
              <div className="flex w-full sm:w-auto flex-wrap items-center gap-2">
                <Switch
                  checked={cat.isActive}
                  onCheckedChange={() => handleToggle(cat.id)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(cat)}
                  className="font-ui"
                >
                  <Pencil size={14} className="mr-1" /> Izmeni
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="font-ui text-muted-foreground hover:text-destructive">
                      <Trash2 size={14} className="mr-1" /> Obrisi
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-serif">Obrisati kategoriju?</AlertDialogTitle>
                      <AlertDialogDescription className="font-ui">
                        Kategoriju možeš obrisati samo ako nema aktivne proizvode.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="font-ui">Otkazati</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(cat.id)} className="bg-destructive text-destructive-foreground font-ui">
                        Obrisi
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </motion.div>
          ))}
        </div>
        </>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingCat ? 'Izmeni kategoriju' : 'Nova kategorija'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="font-ui text-sm mb-1.5 block">Naziv *</Label>
              <Input
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="Mljecni Proizvodi"
                className="font-ui"
              />
            </div>
            <div>
              <Label className="font-ui text-sm mb-1.5 block">Opis</Label>
              <Textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                className="font-ui resize-none"
                rows={3}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.isActive}
                onCheckedChange={v => setFormData({ ...formData, isActive: v })}
              />
              <Label className="font-ui text-sm">Aktivna kategorija</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="font-ui">Otkazati</Button>
            <Button
              onClick={handleSave}
              disabled={saving || !formData.name}
              className="bg-primary text-primary-foreground font-ui"
            >
              {saving && <Loader2 size={16} className="animate-spin mr-2" />}
              Sačuvati
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
